import React from "react";
import { motion, useReducedMotion, type Variants, type Transition } from "framer-motion";
import { navigateTo } from "../utils/navigation";

import fig001 from "../aidata/001.png";
import fig002 from "../aidata/002.png";
import fig003 from "../aidata/003.png";
import cmDarkcircle from "../aidata/004.png";
import cmSkinredness from "../aidata/006.png";
import cmVascular from "../aidata/007.png";
import cmWrinkle from "../aidata/008.png";

export default function AboutPage(): React.JSX.Element {
  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 420, damping: 36, mass: 0.9 };

  const pageIn: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: spring },
  };
  const listIn: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.06,
        delayChildren: prefersReduced ? 0 : 0.04,
      },
    },
  };

  const skinTypeRows = [
    { label: "Normal (0)", p: "0.90", r: "0.72", f1: "0.80", s: "97" },
    { label: "Oily (1)",   p: "0.77", r: "0.88", f1: "0.82", s: "116" },
    { label: "Dry (2)",    p: "0.83", r: "0.85", f1: "0.84", s: "103" },
  ];

  // Labels use the abstracted, user-facing names; the benchmark numbers stay
  // tied to the same internal model classes (darkcircle/skinredness/...).
  const condRows = [
    { label: "Under-eye area",      p: "0.75", r: "0.93", f1: "0.83", s: "67" },
    { label: "Warm tone saturation", p: "0.56", r: "0.67", f1: "0.61", s: "67" },
    { label: "Deep tone saturation", p: "0.41", r: "0.25", f1: "0.31", s: "67" },
    { label: "Expression lines",     p: "0.90", r: "0.94", f1: "0.92", s: "67" },
  ];

  return (
    <motion.div
      variants={listIn}
      initial="hidden"
      animate="visible"
      className="stack-6"
      style={{ overflowX: "clip" }}
    >
      {/* ── Page header ─────────────────────────────────── */}
      <motion.header variants={pageIn}>
        <span className="t-label">AI model</span>
        <h1 className="t-h2" style={{ margin: "var(--s-2) 0 0" }}>
          Our <em className="t-italic t-teal">model,</em> laid bare.
        </h1>
        <p className="t-body t-mute" style={{ marginTop: "var(--s-3)", maxWidth: "58ch" }}>
          A fine-tuned ResNet estimates skin type; a second head estimates visible appearance
          signals (for example tone, texture, expression lines). Below: training curves, validation
          results, and evaluation outputs from held-out test sets — no user images used.
        </p>
      </motion.header>

      {/* ── The flow ────────────────────────────────────── */}
      <motion.section variants={pageIn} className="card">
        <h2 className="t-h3" style={{ margin: 0 }}>How it works</h2>
        <div className="g-3" style={{ marginTop: "var(--s-5)" }}>
          <FlowStep
            n="1"
            title="You capture"
            body="One well-lit selfie. Nothing leaves your device until you confirm."
          />
          <FlowStep
            n="2"
            title="Kivo analyses"
            body="A vision model estimates skin type and reads appearance signals in seconds."
          />
          <FlowStep
            n="3"
            title="You get guidance"
            body="Findings become clear, personalised routines and product matches."
          />
        </div>
      </motion.section>

      {/* ── Overview ────────────────────────────────────── */}
      <motion.section variants={pageIn} className="card">
        <div className="g-3">
          <Stat title="Backbone" value="ResNet (transfer learning)" />
          <Stat
            title="Skin-type dataset"
            value="~3,000 photos"
            hint="Held-out test set: 316 images"
          />
          <Stat
            title="Appearance signals dataset"
            value="~500 photos"
            hint="5 labels, multi-label classification"
          />
        </div>
        <ul
          className="t-body"
          style={{
            marginTop: "var(--s-5)",
            paddingLeft: "var(--s-5)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-2)",
          }}
        >
          <li>Standard augmentations (crop, flip, colour jitter) and early stopping.</li>
          <li>Optimised with AdamW; cosine LR schedule; mixed precision where supported.</li>
          <li>All figures are from held-out validation/test splits — no user images shown.</li>
        </ul>
      </motion.section>

      {/* ── Skin type model ─────────────────────────────── */}
      <motion.section variants={pageIn} className="card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--s-3)",
            flexWrap: "wrap",
          }}
        >
          <h2 className="t-h3">Skin type model results</h2>
          <span className="t-label">Normal · Oily · Dry</span>
        </div>

        <div className="g-3" style={{ marginTop: "var(--s-5)" }}>
          <FigureCard
            src={fig001}
            alt="Training/validation loss curves"
            caption="Training/validation curves"
          />
          <FigureCard
            src={fig002}
            alt="Visual consistency check — overall ≈ 82%"
            caption="Visual consistency check — overall ≈ 82%"
          />
          <FigureCard
            src={fig003}
            alt="Prediction gallery, random samples"
            caption="Prediction gallery (random samples)"
          />
        </div>

        {/* mobile report cards */}
        <div className="sm:hidden" style={{ marginTop: "var(--s-5)" }}>
          <h3 className="t-h3" style={{ marginBottom: "var(--s-3)" }}>
            Classification report (n=316)
          </h3>
          <p className="t-small t-mute" style={{ marginBottom: "var(--s-3)" }}>
            Internal consistency benchmark on a diverse appearance dataset. For cosmetic & educational use only.
          </p>
          <div className="stack-3">
            {skinTypeRows.map((r) => (
              <MetricCard key={r.label} {...r} />
            ))}
            <div
              style={{
                background: "var(--c-sand)",
                borderRadius: "var(--r-sm)",
                padding: "var(--s-3)",
              }}
            >
              <p className="t-small" style={{ fontWeight: 500 }}>Overall consistency</p>
              <div
                className="t-small t-mute"
                style={{ marginTop: "var(--s-1)", display: "flex", justifyContent: "space-between" }}
              >
                <span>Overall</span>
                <span>0.82</span>
              </div>
            </div>
          </div>
        </div>

        {/* desktop report table */}
        <div className="hidden sm:block" style={{ marginTop: "var(--s-5)" }}>
          <h3 className="t-h3" style={{ marginBottom: "var(--s-3)" }}>
            Classification report (test set, n=316)
          </h3>
          <p className="t-small t-mute" style={{ marginBottom: "var(--s-3)" }}>
            Internal consistency benchmark on a diverse appearance dataset. For cosmetic & educational use only.
          </p>
          <div style={{ overflowX: "auto" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Precision</th>
                  <th>Recall</th>
                  <th>F1</th>
                  <th>Support</th>
                </tr>
              </thead>
              <tbody>
                <Row label="Normal (0)" p="0.90" r="0.72" f1="0.80" s="97" />
                <Row label="Oily (1)"   p="0.77" r="0.88" f1="0.82" s="116" />
                <Row label="Dry (2)"    p="0.83" r="0.85" f1="0.84" s="103" />
                <tr className="row-sep">
                  <td style={{ fontWeight: 500 }}>Overall consistency</td>
                  <td colSpan={3}>0.82</td>
                  <td>316</td>
                </tr>
                <Row label="Macro avg"    p="0.83" r="0.82" f1="0.82" s="316" dim />
                <Row label="Weighted avg" p="0.83" r="0.82" f1="0.82" s="316" dim />
              </tbody>
            </table>
          </div>
        </div>
      </motion.section>

      {/* ── Conditions model ────────────────────────────── */}
      <motion.section variants={pageIn} className="card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "var(--s-3)",
            flexWrap: "wrap",
          }}
        >
          <h2 className="t-h3">Appearance signals</h2>
          <span className="t-label">
            Under-eye area · Warm tone saturation · Deep tone saturation · Expression lines
          </span>
        </div>

        {/* mobile report cards */}
        <div className="sm:hidden" style={{ marginTop: "var(--s-4)" }}>
          <p className="t-small t-mute" style={{ marginBottom: "var(--s-3)" }}>
            Internal consistency benchmark on a diverse appearance dataset. For cosmetic & educational use only.
          </p>
          <div className="stack-3">
            {condRows.map((r) => (
              <MetricCard key={r.label} {...r} />
            ))}
            <div
              style={{
                background: "var(--c-sand)",
                borderRadius: "var(--r-sm)",
                padding: "var(--s-3)",
              }}
            >
              <p className="t-small" style={{ fontWeight: 500 }}>Averages</p>
              <p className="t-small t-mute" style={{ marginTop: "var(--s-1)" }}>
                Micro: P 0.60 · R 0.69 · F1 0.64 (n=335)
              </p>
            </div>
          </div>
        </div>

        {/* desktop report table */}
        <div className="hidden sm:block" style={{ marginTop: "var(--s-4)", overflowX: "auto" }}>
          <p className="t-small t-mute" style={{ marginBottom: "var(--s-3)" }}>
            Internal consistency benchmark on a diverse appearance dataset. For cosmetic & educational use only.
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Precision</th>
                <th>Recall</th>
                <th>F1</th>
                <th>Support</th>
              </tr>
            </thead>
            <tbody>
              <Row label="Under-eye area"      p="0.75" r="0.93" f1="0.83" s="67" />
              <Row label="Warm tone saturation" p="0.56" r="0.67" f1="0.61" s="67" />
              <Row label="Deep tone saturation" p="0.41" r="0.25" f1="0.31" s="67" />
              <Row label="Expression lines"     p="0.90" r="0.94" f1="0.92" s="67" />
              <tr className="row-sep">
                <td style={{ fontWeight: 500 }}>Micro avg</td>
                <td>0.60</td>
                <td>0.69</td>
                <td>0.64</td>
                <td>335</td>
              </tr>
              <Row label="Macro avg"    p="0.60" r="0.69" f1="0.63" s="335" dim />
              <Row label="Weighted avg" p="0.60" r="0.69" f1="0.63" s="335" dim />
              <Row label="Samples avg"  p="0.60" r="0.68" f1="0.62" s="335" dim />
            </tbody>
          </table>
        </div>

        {/* per-label confusion matrices */}
        <div className="g-3" style={{ marginTop: "var(--s-5)", gap: "var(--s-4)" }}>
          <MiniFigure src={cmDarkcircle} label="Under-eye area" />
          <MiniFigure src={cmSkinredness} label="Warm tone saturation" />
          <MiniFigure src={cmVascular} label="Deep tone saturation" />
          <MiniFigure src={cmWrinkle} label="Expression lines" />
        </div>

        <p className="t-small t-mute" style={{ marginTop: "var(--s-4)" }}>
          In multi-label classification, each visual signal is evaluated independently. Performance
          varies — a function of visual distinctiveness and dataset size. We continue to improve
          with additional data, augmentation, and threshold tuning.
        </p>
      </motion.section>

      {/* ── What this means ─────────────────────────────── */}
      <motion.section variants={pageIn} className="card">
        <h3 className="t-h3">What this means for you</h3>
        <ul
          className="t-body"
          style={{
            marginTop: "var(--s-3)",
            paddingLeft: "var(--s-5)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--s-2)",
          }}
        >
          <li>
            <strong>Fast, consistent assessments</strong> from a model trained on thousands of
            annotated examples.
          </li>
          <li>
            <strong>Transparent metrics</strong> — we publish accuracy, precision/recall, and
            per-label confusion matrices.
          </li>
          <li>
            <strong>Privacy-first</strong> — your photos are processed under our Privacy Policy
            and never shared.
          </li>
        </ul>
        <div
          className="row-3"
          style={{ marginTop: "var(--s-5)", flexWrap: "wrap", gap: "var(--s-3)" }}
        >
          <button className="btn btn--primary" onClick={() => { navigateTo("/new"); }}>
            Begin a scan →
          </button>
          <button className="btn btn--ghost" onClick={() => { navigateTo("/history"); }}>
            View my results
          </button>
          <button className="btn btn--ghost" onClick={() => { navigateTo("/privacy"); }}>
            Privacy policy
          </button>
        </div>
        <p className="t-small t-mute" style={{ marginTop: "var(--s-4)" }}>
          For cosmetic & educational use only. Not a medical service.
        </p>
      </motion.section>
    </motion.div>
  );
}

/* ── Sub-components ──────────────────────────────────── */

function FlowStep({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div style={{ display: "flex", gap: "var(--s-3)", alignItems: "flex-start" }}>
      <div
        style={{
          flexShrink: 0,
          width: 38,
          height: 38,
          borderRadius: 13,
          background: "var(--c-teal-weak)",
          color: "var(--c-deep-teal)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          font: "500 15px/1 var(--f-text)",
        }}
      >
        {n}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p className="t-small" style={{ fontWeight: 600, margin: 0 }}>{title}</p>
        <p className="t-small t-mute" style={{ margin: "var(--s-1) 0 0" }}>{body}</p>
      </div>
    </div>
  );
}

function Stat({ title, value, hint }: { title: string; value: string; hint?: string }) {
  return (
    <div
      style={{
        background: "var(--c-sand)",
        borderRadius: "var(--r-sm)",
        padding: "var(--s-4)",
      }}
    >
      <p className="t-small t-mute">{title}</p>
      <p className="t-h3" style={{ margin: "var(--s-1) 0 0" }}>{value}</p>
      {hint && (
        <p className="t-small t-mute" style={{ marginTop: "var(--s-1)" }}>{hint}</p>
      )}
    </div>
  );
}

function MetricCard({
  label, p, r, f1, s,
}: { label: string; p: string; r: string; f1: string; s: string }) {
  return (
    <div
      style={{
        background: "var(--c-sand)",
        borderRadius: "var(--r-sm)",
        padding: "var(--s-3)",
      }}
    >
      <p className="t-small" style={{ fontWeight: 500 }}>{label}</p>
      <div
        className="t-small t-mute"
        style={{
          marginTop: "var(--s-1)",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--s-1) 0",
        }}
      >
        <span>Precision</span><span style={{ textAlign: "right" }}>{p}</span>
        <span>Recall</span>   <span style={{ textAlign: "right" }}>{r}</span>
        <span>F1</span>       <span style={{ textAlign: "right" }}>{f1}</span>
        <span>Support</span>  <span style={{ textAlign: "right" }}>{s}</span>
      </div>
    </div>
  );
}

function Row({
  label, p, r, f1, s, dim,
}: { label: string; p: string; r: string; f1: string; s: string; dim?: boolean }) {
  return (
    <tr className={dim ? "row-dim" : ""}>
      <td>{label}</td>
      <td>{p}</td>
      <td>{r}</td>
      <td>{f1}</td>
      <td>{s}</td>
    </tr>
  );
}

function FigureCard({ src, alt, caption }: { src: string; alt: string; caption: string }) {
  return (
    <div className="frame" style={{ display: "flex", flexDirection: "column" }}>
      <img
        src={src}
        alt={alt}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          aspectRatio: "16 / 9",
          objectFit: "contain",
          background: "var(--c-cream-weak)",
        }}
        loading="lazy"
      />
      <p
        className="t-small t-mute"
        style={{ padding: "var(--s-2) var(--s-3) var(--s-3)" }}
      >
        {caption}
      </p>
    </div>
  );
}

function MiniFigure({ src, label }: { src: string; label: string }) {
  return (
    <div className="frame" style={{ display: "flex", flexDirection: "column" }}>
      <img
        src={src}
        alt={`${label} confusion matrix`}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          aspectRatio: "4 / 3",
          objectFit: "contain",
          background: "var(--c-cream-weak)",
        }}
        loading="lazy"
      />
      <p
        className="t-small t-mute"
        style={{ padding: "var(--s-2) var(--s-3)", textAlign: "center" }}
      >
        {label} — CM
      </p>
    </div>
  );
}
